import {ChangeDetectorRef, Component, inject, QueryList, ViewChildren} from '@angular/core';
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {CommonModule} from "@angular/common";
import {DesignSystemModule} from "../../../../../design-system/design-system.module";
import {
	Data,
	DataApplicationForm,
	DataApplicationRole, DataApplicationRolesForm,
	DataRoles,
	DataService
} from "../../../../../../services/back/data.service";
import {distinctUntilChanged, finalize, forkJoin, Observable, of, Subscription, switchMap, tap} from "rxjs";
import {CurrentTenantService} from "../../../../../../services/front/current-tenant.service";
import {Router} from "@angular/router";
import {
	ApplicationDetailData,
	ApplicationDetailService
} from "../../../../../../services/front/application-detail.service";
import {filter} from "rxjs/operators";
import {DATA_URL} from "../../../../../../models/home/navigation.model";
import {ButtonWidgetComponent} from "../../../../../design-system/button-widget/button-widget.component";
import {MatDialog} from "@angular/material/dialog";
import {AddDataToAppComponent} from "./add-data-to-app/add-data-to-app.component";
import {SnackbarService} from "../../../../../../services/front/snackbar.service";
import {DashboardPageLoaderComponent} from "../../../../../global/title-content-loader/dashboard-page-loader.component";
import {FormControl} from "@angular/forms";
import {MultiSelectComponent} from "../../../../../design-system/multi-select/multi-select.component";

@Component({
  selector: 'app-application-data',
	imports: [
		TranslateModule,
		CommonModule,
		DesignSystemModule,
		ButtonWidgetComponent,
		DashboardPageLoaderComponent
	],
  templateUrl: './application-data.component.html',
  styleUrl: './application-data.component.scss'
})
export class ApplicationDataComponent {
	initialized: boolean = false;
	_initializing: boolean;
	_loading: boolean;

	tenantId: string;
	applicationId: string;
	isEditor: boolean = false;

	tenantData: Data[] = [];
	dataList: DataRoles[] = [];
	dataRoleList: DataApplicationRole[] = Object.values(DataApplicationRole).filter(role => role !== DataApplicationRole.REFERENCE);

	dataService = inject(DataService);
	currentTenantService = inject(CurrentTenantService);
	applicationDetailService = inject(ApplicationDetailService);
	router = inject(Router);
	dialog = inject(MatDialog);
	snackbarService = inject(SnackbarService);
	translateService = inject(TranslateService);
	cdr = inject(ChangeDetectorRef);

	subscriptions: Subscription = new Subscription();
	formSubscription: Subscription = new Subscription();

	dataControls: Map<string, FormControl<DataApplicationRole[]>> = new Map();

	@ViewChildren(MultiSelectComponent) multiSelectComponents: QueryList<MultiSelectComponent>;

	ngOnInit(): void {
		this.subscriptions.add(this.applicationDetailService.getInitializingChanges()
			.subscribe(initializing => this._initializing = initializing));
		this.subscriptions.add(this.applicationDetailService.getApplicationDetailDataChanges()
			.pipe(tap(data => this.setApplicationDetailData(data)))
			.subscribe(() => this.initialize()));
	}

	private setApplicationDetailData(data: ApplicationDetailData): void {
		this.tenantId = data.tenantId;
		this.isEditor = data.isEditor;
		this.applicationId = data.instance.applicationId;
	}

	initialize(): void {
		this.subscriptions.add(this.switchLoading()
			.pipe(
				switchMap(() => forkJoin([
					this.dataService.getApplicationData(this.tenantId, this.applicationId),
					this.dataService.getAllTenantData(this.tenantId)
				])),
				tap(([dataList, tenantData]) => {
					this.dataList = dataList
						.sort((a, b) => {
							const aHasReference = a.roles.includes(DataApplicationRole.REFERENCE);
							const bHasReference = b.roles.includes(DataApplicationRole.REFERENCE);

							if (aHasReference && !bHasReference) return -1;
							if (!aHasReference && bHasReference) return 1;

							if (a.roles.length !== b.roles.length) {
								return b.roles.length - a.roles.length;
							}

							return a.data.name.localeCompare(b.data.name);
						});
					this.tenantData = tenantData;
					this.setFormControls();
				}),
				finalize(() => this.switchLoading()))
			.subscribe(() => this.initialized = true));
	}

	setFormControls(): void {
		this.dataControls.clear();
		this.dataList.forEach(data => {
			const dataId = data.data.id;

			const initialValues = data.roles && data.roles.length ? data.roles : [];

			const control = new FormControl<DataApplicationRole[]>(initialValues, { nonNullable: true });

			this.dataControls.set(dataId, control);

			this.formSubscription.add(control.valueChanges
				.pipe(
					distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
				).subscribe((data) => this.saveRole(dataId, data)));
		});
	}

	getControlForData(dataId: string): FormControl {
		if (!this.dataControls.has(dataId)) {
			this.dataControls.set(dataId, new FormControl([], { nonNullable: true }));
		}
		return this.dataControls.get(dataId)!;
	}

	saveRole(dataId: string, roles: DataApplicationRole[]): void {
		this._loading = true;

		const form: DataApplicationRolesForm = {
			roles: roles
		}

		this.dataService.updateDataApplicationRoles(this.tenantId, dataId, this.applicationId, form).pipe(
			filter(success => success),
			tap(() => this.snackbarService.show(this.translateService.instant('page.application.detail.update.success'))),
			finalize(() => this._loading = false)
		).subscribe(() => this.applicationDetailService.refreshApplicationInstance());
	}

	goToData(dataId: string): void {
		this.router.navigate([DATA_URL], {
			queryParams: {
				tenant: this.tenantId,
				dataId: dataId
			}
		});
	}

	removeRole(role: DataApplicationRole, dataId: string) {
		const control = this.dataControls.get(dataId);
		if (control) {
			const currentValue = control.value;
			control.setValue(currentValue.filter(r => r !== role));

			setTimeout(() => {
				this.multiSelectComponents.forEach(msComponent => {
					msComponent.forceUpdate();
				});
				this.cdr.detectChanges();
			});
		}
	}

	openDialog(): void {
		this.dialog.open(AddDataToAppComponent, {
			position: {right: '162.5px'},
			width: '475px',
			data: {
				tenantData: this.tenantData,
				applicationData: this.dataList.map(data => data.data),
			}
		})
			.afterClosed()
			.pipe(
				filter((result: {data: Data[], result: boolean}) => result.result),
				tap((result: {data: Data[], result: boolean}) => {
					const added = result.data
						.filter(data => !this.dataList.find(appData => appData.data.id === data.id));
					const removed = this.dataList
						.filter(appData => !result.data.find(data => data.id === appData.data.id))
						.map(appData => appData.data);
					if (added.length + removed.length > 0) {
						this.updateApplicationData(added, removed);
					}
				})
			).subscribe();
	}

	updateApplicationData(added: Data[], removed: Data[]): void {
		const form: DataApplicationForm = {
			added: added.map(app => app.id),
			removed: removed.map(app => app.id)
		};
		this.dataService.updateApplicationData(this.tenantId, this.applicationId, form)
			.pipe(
				filter(success => success),
				tap(() => this.snackbarService.show(this.translateService.instant('page.application.detail.update.success')))
			).subscribe(() => this.applicationDetailService.refreshApplicationInstance());
	}

	private switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({});
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.formSubscription.unsubscribe();
	}

	protected readonly DataApplicationRole = DataApplicationRole;
}
